import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useCallback, useEffect, useState } from 'react';

import { STORAGE_KEYS } from '@/constants/storageKeys';
import { Sentry } from '@/util/sentry';

async function readStoredValue(): Promise<string | null> {
  if (isWeb) {
    return typeof window !== 'undefined' && window.localStorage
      ? window.localStorage.getItem(STORAGE_KEYS.FEATURES_ANNOUNCEMENT_BANNER_ENABLED)
      : null;
  }

  return AsyncStorage.getItem(STORAGE_KEYS.FEATURES_ANNOUNCEMENT_BANNER_ENABLED);
}

async function writeStoredValue(enabled: boolean) {
  if (isWeb) {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(
        STORAGE_KEYS.FEATURES_ANNOUNCEMENT_BANNER_ENABLED,
        String(enabled)
      );
    }

    return;
  }

  await AsyncStorage.setItem(STORAGE_KEYS.FEATURES_ANNOUNCEMENT_BANNER_ENABLED, String(enabled));
}

/**
 * A staff member's device-only choice to hide the announcement banner.
 *
 * The banner reads the same storage key in `useAnnouncements` when it mounts, so a change shows
 * after a reload. On web, other open tabs pick it up at once through the storage event, which a
 * browser does not fire in the tab that made the change. A missing value means shown. `enabled` is
 * null until the stored value has been read, so a switch can stay disabled rather than flip once
 * it loads.
 */
export function useAnnouncementBannerSetting() {
  const [enabled, setEnabledState] = useState<boolean | null>(null);

  useEffect(() => {
    let isCurrent = true;

    readStoredValue()
      .then((value) => {
        if (isCurrent) {
          setEnabledState(value === null ? true : value === 'true');
        }
      })
      .catch(() => {
        if (isCurrent) {
          setEnabledState(true);
        }
      });

    return () => {
      isCurrent = false;
    };
  }, []);

  const setEnabled = useCallback(
    (next: boolean) => {
      const previous = enabled;

      setEnabledState(next);

      writeStoredValue(next).catch((error: unknown) => {
        // The switch must not show a choice that was never saved.
        setEnabledState(previous);

        Sentry.captureException(error, {
          tags: { component: 'PrimumSection', source: 'announcement-banner-setting' },
          extra: {
            errorMessage: error instanceof Error ? error.message : String(error),
            storageKey: STORAGE_KEYS.FEATURES_ANNOUNCEMENT_BANNER_ENABLED,
          },
        });
      });
    },
    [enabled]
  );

  return { enabled, setEnabled };
}
